import { useState } from "react";
import classNames from "classnames";

// Quick-reference tables, computed from the TOW formulas so they never
// drift from an edited data file.

const range = (from, to) =>
  Array.from({ length: to - from + 1 }, (_, i) => from + i);

// Melee to hit: attacker WS vs defender WS.
const toHitMelee = (attacker, defender) => {
  if (attacker > defender) return "3+";
  if (defender > attacker * 2) return "5+";
  return "4+";
};

// To wound: S vs T.
const toWound = (s, t) => {
  const diff = s - t;
  if (diff >= 2) return "2+";
  if (diff === 1) return "3+";
  if (diff === 0) return "4+";
  if (diff === -1) return "5+";
  return "6+";
};

// Armour save modifier by strength.
const armourMod = (s) => (s <= 3 ? "—" : `-${s - 3}`);

const WS_RANGE = range(1, 10);
const ST_RANGE = range(1, 10);

const TABS = [
  { id: "hit", label: "To Hit" },
  { id: "wound", label: "To Wound" },
  { id: "save", label: "Saves" },
];

export const ReferenceDrawer = ({ open, onClose }) => {
  const [tab, setTab] = useState("hit");

  if (!open) return null;

  return (
    <div className="battle-drawer">
      <div className="battle-drawer__bar">
        {TABS.map(({ id, label }) => (
          <button
            key={id}
            type="button"
            className={classNames(
              "battle-drawer__tab",
              tab === id && "battle-drawer__tab--selected",
            )}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
        <button
          type="button"
          className="battle-drawer__close"
          onClick={onClose}
          aria-label="Close reference"
        >
          ✕
        </button>
      </div>

      <div className="battle-drawer__content">
        {tab === "hit" && (
          <>
            <h3>Melee — attacker WS vs defender WS</h3>
            <div className="battle-drawer__scroll">
              <table className="battle-drawer__table">
                <thead>
                  <tr>
                    <th>WS</th>
                    {WS_RANGE.map((ws) => (
                      <th key={ws}>{ws}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {WS_RANGE.map((attacker) => (
                    <tr key={attacker}>
                      <th>{attacker}</th>
                      {WS_RANGE.map((defender) => (
                        <td key={defender}>{toHitMelee(attacker, defender)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <h3>Shooting — ballistic skill</h3>
            <div className="battle-drawer__scroll">
              <table className="battle-drawer__table">
                <thead>
                  <tr>
                    {range(1, 5).map((bs) => (
                      <th key={bs}>BS {bs}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {range(1, 5).map((bs) => (
                      <td key={bs}>{7 - bs}+</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="battle-drawer__note">
              Apply modifiers (long range, moving, cover…) after the base roll.
            </p>
          </>
        )}

        {tab === "wound" && (
          <>
            <h3>To wound — S (rows) vs T (columns)</h3>
            <div className="battle-drawer__scroll">
              <table className="battle-drawer__table">
                <thead>
                  <tr>
                    <th>S/T</th>
                    {ST_RANGE.map((t) => (
                      <th key={t}>{t}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ST_RANGE.map((s) => (
                    <tr key={s}>
                      <th>{s}</th>
                      {ST_RANGE.map((t) => (
                        <td key={t}>{toWound(s, t)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        {tab === "save" && (
          <>
            <h3>Armour save modifier by Strength</h3>
            <div className="battle-drawer__scroll">
              <table className="battle-drawer__table">
                <thead>
                  <tr>
                    {ST_RANGE.map((s) => (
                      <th key={s}>S{s}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {ST_RANGE.map((s) => (
                      <td key={s}>{armourMod(s)}</td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="battle-drawer__note">
              Ward and Regeneration saves are never modified. Misfire and
              artillery results: check your rulebook — add them to this drawer
              once you have the page in front of you (ReferenceDrawer.jsx).
            </p>
          </>
        )}
      </div>
    </div>
  );
};
